import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  TemplateRef,
  computed,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { CustomEventModel } from '@models/custom-event.model';
import { filter, take } from 'rxjs';
import { CustomEventsSelectors } from '@states/custom-events/custom-events.selector-types';
import * as _ from 'lodash';
import { UIInputStyle } from '@enums/shared.enum';
import * as MenuActions from '@states/menu/menu.actions';
import { CustomEventsActions } from '@states/custom-events/custom-events.action-types';
import { MenuKey } from '@enums/menu.enum';
import { CustomEventsEffect } from '@effects/custom-events.effect';
import { routerSegments } from '@consts/routes';
import { SharedActions } from '@states/shared/shared.action-types';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormsModule } from '@angular/forms';
import { UiKitModule } from '../../../../../shared/ui-kit/ui-kit.module';
import { MatSelectModule } from '@angular/material/select';
import { UiDatasetTableComponent } from '@ui/lib/ui-kit/ui-dataset-table';
import {
  CustomEventFieldCellContext,
  createCustomEventsEditTableConfig,
} from './custom-events-edit-table.util';
import { UiButtonComponent } from '@ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-custom-events-edit',
  templateUrl: './custom-events-edit.component.html',
  styleUrls: ['./custom-events-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    FormsModule,
    UiKitModule,
    TranslatePipe,
    FormsModule,
    MatSelectModule,
    UiButtonComponent,
    UiDatasetTableComponent,
  ],
})
export class CustomEventsEditComponent implements OnInit {
  public CustomEventFieldType = CustomEventModel.CustomEventFieldType;

  protected readonly nameCellTemplate =
    viewChild<TemplateRef<CustomEventFieldCellContext>>('nameCell');
  protected readonly typeCellTemplate =
    viewChild<TemplateRef<CustomEventFieldCellContext>>('typeCell');

  selectedCustomEvent: CustomEventModel.CustomEvent = {
    name: '',
    videoLength: 10,
    fields: [
      {
        name: '',
        type: undefined,
      },
    ],
  };

  public readonly tableConfig = computed(() => {
    const nameCellTemplate = this.nameCellTemplate();
    const typeCellTemplate = this.typeCellTemplate();

    if (!nameCellTemplate || !typeCellTemplate) {
      return null;
    }

    return createCustomEventsEditTableConfig({
      nameCellTemplate,
      typeCellTemplate,
    });
  });

  constructor(
    private store$: Store,
    private route: ActivatedRoute,
    private customEventsEffect: CustomEventsEffect,
    private router: Router,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private destroyRef: DestroyRef,
  ) {}

  public get isValid() {
    if (this.selectedCustomEvent?.videoLength < 10 || this.selectedCustomEvent.videoLength > 180) {
      this.store$.dispatch(
        SharedActions.showMessage({ error: this.translateService.instant('TOASTS.VIDEO_LENGTH') }),
      );
      return false;
    }
    for (let field of this.selectedCustomEvent.fields) {
      if (!field.name || (!field.type && field.type !== 0)) {
        this.store$.dispatch(
          SharedActions.showMessage({ error: this.translateService.instant('TOASTS.ALL_FIELDS_NAME_TYPE') }),
        );
        return false;
      }
    }

    return true;
  }

  public ngOnInit(): void {
    this.store$.dispatch(SharedActions.setIsLoading({ isLoading: true }));
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      if (res['id']) {
        this.store$.dispatch(
          MenuActions.setActiveLevel2Menu({
            activeLevel2: MenuKey.eventTypeEdit,
          }),
        );
        this.store$
          .select(CustomEventsSelectors.selectCustomEventById(res['id']))
          .pipe(take(1))
          .subscribe((customEvent: CustomEventModel.CustomEvent) => {
            if (!customEvent) {
              this.store$.dispatch(CustomEventsActions.getCustomEvents());
              this.store$.dispatch(SharedActions.setIsLoading({ isLoading: true }));
              this.store$
                .select(CustomEventsSelectors.selectCustomEventById(res['id']))
                .pipe(
                  filter((event) => !!event),
                  take(1),
                )
                .subscribe((customEvent: CustomEventModel.CustomEvent) => {
                  this.selectedCustomEvent = _.cloneDeep(customEvent);
                  this.cdr.markForCheck();
                });
            } else {
              this.selectedCustomEvent = _.cloneDeep(customEvent);
              this.cdr.markForCheck();
            }
          });
      } else {
        this.store$.dispatch(
          MenuActions.setActiveLevel2Menu({
            activeLevel2: MenuKey.eventTypeCreate,
          }),
        );
      }
    });

    this.customEventsEffect.pressSave$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.selectedCustomEvent?.name) {
        this.selectedCustomEvent.name = 'Untitled event tag';
      }
      if (!this.isValid) {
        return;
      }

      const customEvent = _.cloneDeep(this.selectedCustomEvent);
      if (!customEvent._id) {
        this.store$.dispatch(CustomEventsActions.addCustomEvent({ customEvent }));
      } else {
        this.store$.dispatch(CustomEventsActions.editCustomEvent({ customEvent }));
      }
    });

    this.customEventsEffect.addEditCustomEventSuccess$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.router.navigate([routerSegments.database, routerSegments.eventTypes]);
    });
  }

  removeField(field: CustomEventModel.CustomEventField) {
    const idx = this.selectedCustomEvent.fields.indexOf(field);
    if (idx === -1) {
      return;
    }
    this.selectedCustomEvent.fields.splice(idx, 1);
    this.selectedCustomEvent.fields = [...this.selectedCustomEvent.fields];
  }

  public onRowAction(event: {
    actionId: string;
    row: CustomEventModel.CustomEventField;
  }): void {
    if (event.actionId === 'delete') {
      this.removeField(event.row);
    }
  }

  addField() {
    if (this.selectedCustomEvent.fields.length >= 10) return;
    this.selectedCustomEvent.fields = [
      ...this.selectedCustomEvent.fields,
      {
        name: '',
        type: undefined,
      },
    ];
  }

  public readonly UIInputStyle = UIInputStyle;
}
